import React, { useState, useCallback, useMemo } from 'react';
import BuilderToolbar from './BuilderToolbar';
import WidgetLibrary from './WidgetLibrary';
import ElementInspector from './ElementInspector';
import BuilderCanvas from './BuilderCanvas';
import NavigatorTree from './NavigatorTree';
import MediaPickerModal from './MediaPickerModal';
import { useBuilderHistory } from './hooks/useBuilderHistory';
import { useBuilderApproval, computeDocumentFingerprint } from './hooks/useBuilderApproval';
import {
  createDefaultSection,
  createDefaultContainer,
  createDefaultWidget,
  duplicateNode,
  findNodeById,
  validateDocument,
  DEFAULT_HEADER_CONFIG,
  DEFAULT_FOOTER_CONFIG,
} from './schema/pageDocumentModel';
import { canonicalToLegacy } from './schema/legacyAdapter';

export default function PageBuilderEditor({
  initialDocument,
  onSaveDocument,
  onSave,
  onClose,
  client = null,
  isSaving = false,
}) {
  // 1. History & Document State
  const {
    document: doc,
    setDocument,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useBuilderHistory(initialDocument);

  // 2. Editor UI State
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [insertionContainerId, setInsertionContainerId] = useState(null);
  const [viewport, setViewport] = useState('desktop'); // desktop, tablet, mobile
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isNavigatorOpen, setIsNavigatorOpen] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [mediaPickerCallback, setMediaPickerCallback] = useState(null);
  const [notification, setNotification] = useState(null);

  // 3. Strict Preview Approval
  const {
    isApproved,
    canApprove,
    validationErrors,
    approvePreview,
    resetApproval,
  } = useBuilderApproval(doc, false, isSaving);

  const showToast = (type, text) => {
    setNotification({ type, text });
    window.setTimeout(() => setNotification(null), 3500);
  };

  // Currently selected node object
  const selectedNode = useMemo(() => {
    if (!selectedNodeId) return null;
    if (selectedNodeId === 'website-header') {
      return {
        id: 'website-header',
        type: 'header',
        name: 'Website Header & Navigation',
        content: doc.header || DEFAULT_HEADER_CONFIG,
      };
    }
    if (selectedNodeId === 'website-footer') {
      return {
        id: 'website-footer',
        type: 'footer',
        name: 'Website Footer',
        content: doc.footer || DEFAULT_FOOTER_CONFIG,
      };
    }
    const found = findNodeById(doc.sections, selectedNodeId);
    return found?.node || null;
  }, [doc.sections, doc.header, doc.footer, selectedNodeId]);

  // ----------------------------------------------------
  // MUTATION HANDLERS
  // ----------------------------------------------------

  // Update a node by deep traversal
  const handleUpdateNode = useCallback((updatedNode) => {
    if (updatedNode.id === 'website-header') {
      setDocument((prevDoc) => ({
        ...prevDoc,
        header: {
          ...(prevDoc.header || DEFAULT_HEADER_CONFIG),
          ...(updatedNode.content || {}),
        },
      }));
      return;
    }
    if (updatedNode.id === 'website-footer') {
      setDocument((prevDoc) => ({
        ...prevDoc,
        footer: {
          ...(prevDoc.footer || DEFAULT_FOOTER_CONFIG),
          ...(updatedNode.content || {}),
        },
      }));
      return;
    }
    setDocument((prevDoc) => {
      const clone = JSON.parse(JSON.stringify(prevDoc));
      const found = findNodeById(clone.sections, updatedNode.id);
      if (found) {
        Object.assign(found.node, updatedNode);
      }
      return clone;
    });
  }, [setDocument]);

  // Add Section to the document
  const handleAddSection = useCallback(() => {
    const newSec = createDefaultSection(`Section ${(doc.sections || []).length + 1}`);
    setDocument((prevDoc) => ({
      ...prevDoc,
      sections: [...(prevDoc.sections || []), newSec],
    }));
    setSelectedNodeId(newSec.id);
    showToast('success', 'New section added');
  }, [doc.sections, setDocument]);

  const handleAddContainer = useCallback((sectionId) => {
    const container = createDefaultContainer('2-column', 'New Columns');
    setDocument((prevDoc) => {
      const clone = JSON.parse(JSON.stringify(prevDoc));
      const section = findNodeById(clone.sections, sectionId);
      if (!section || section.node.type !== 'section') return prevDoc;
      section.node.children = [...(section.node.children || []), container];
      return clone;
    });
    setSelectedNodeId(container.id);
  }, [setDocument]);
  // Add Widget to a Container or selected container
  const handleAddWidget = useCallback((widgetType) => {
    const newWidget = createDefaultWidget(widgetType);

    setDocument((prevDoc) => {
      const clone = JSON.parse(JSON.stringify(prevDoc));
      if (insertionContainerId) {
        const target = findNodeById(clone.sections, insertionContainerId);
        if (target?.node.type === 'container') {
          target.node.children = [...(target.node.children || []), newWidget];
          return clone;
        }
      }
      if (selectedNode?.type === 'container') {
        const found = findNodeById(clone.sections, selectedNode.id);
        if (found) {
          found.node.children = [...(found.node.children || []), newWidget];
          return clone;
        }
      }
      // If a widget is selected, insert after it in parent container
      if (selectedNode && selectedNode.type !== 'section' && selectedNode.type !== 'container') {
        const found = findNodeById(clone.sections, selectedNode.id);
        if (found && found.parentArray) {
          found.parentArray.splice(found.index + 1, 0, newWidget);
          return clone;
        }
      }
      // Otherwise, add to the first container of the last section, or create section
      if (clone.sections.length === 0) {
        const sec = createDefaultSection('Section 1');
        sec.children[0].children.push(newWidget);
        clone.sections.push(sec);
        return clone;
      }
      const lastSec = clone.sections[clone.sections.length - 1];
      if (!lastSec.children || lastSec.children.length === 0) {
        lastSec.children = [createDefaultContainer('1-column')];
      }
      lastSec.children[0].children.push(newWidget);
      return clone;
    });

    setInsertionContainerId(null);
    setSelectedNodeId(newWidget.id);
    showToast('success', `${widgetType} added`);
  }, [selectedNode, insertionContainerId, setDocument]);

  const handleInsertSavedBlock = useCallback((block) => {
    const bType = block.block_type || 'card';
    const widgetType = bType === 'action' ? 'action_button' : bType === 'note' ? 'note_box' : 'card';
    const widget = createDefaultWidget(widgetType, {
      name: block.title || 'Saved Card',
      content: {
        eyebrow: block.eyebrow || '',
        title: block.title || '',
        description: block.description || '',
        image: block.image || '',
        alt: block.alt || '',
        href: block.href || '',
        cta_label: block.cta_label || '',
        status_text: block.status_text || '',
      },
    });

    setDocument((prevDoc) => {
      const clone = JSON.parse(JSON.stringify(prevDoc));
      if (insertionContainerId) {
        const target = findNodeById(clone.sections, insertionContainerId);
        if (target?.node.type === 'container') {
          target.node.children = [...(target.node.children || []), widget];
          return clone;
        }
      }
      if (selectedNode?.type === 'container') {
        const found = findNodeById(clone.sections, selectedNode.id);
        if (found) {
          found.node.children = [...(found.node.children || []), widget];
          return clone;
        }
      }
      if (selectedNode && selectedNode.type !== 'section' && selectedNode.type !== 'container') {
        const found = findNodeById(clone.sections, selectedNode.id);
        if (found && found.parentArray) {
          found.parentArray.splice(found.index + 1, 0, widget);
          return clone;
        }
      }
      if (clone.sections.length === 0) {
        const sec = createDefaultSection('Card Section');
        sec.children[0].children.push(widget);
        clone.sections.push(sec);
        return clone;
      }
      const lastSec = clone.sections[clone.sections.length - 1];
      if (!lastSec.children || lastSec.children.length === 0) {
        lastSec.children = [createDefaultContainer('1-column')];
      }
      lastSec.children[0].children.push(widget);
      return clone;
    });

    setInsertionContainerId(null);
    setSelectedNodeId(widget.id);
    showToast('success', `"${block.title || 'Card'}" inserted into page!`);
  }, [selectedNode, insertionContainerId, setDocument]);

  const handleAddWidgetToContainer = useCallback((containerId) => {
    setInsertionContainerId(containerId);
    setSelectedNodeId(null);
    showToast('info', 'Choose an element from the left panel to insert');
  }, []);

  // Move node up or down within its parent array
  const handleMoveNode = useCallback((nodeId, direction) => {
    setDocument((prevDoc) => {
      const clone = JSON.parse(JSON.stringify(prevDoc));
      const found = findNodeById(clone.sections, nodeId);
      if (!found || !found.parentArray) return prevDoc;

      const idx = found.index;
      const targetIdx = idx + direction;
      if (targetIdx < 0 || targetIdx >= found.parentArray.length) return prevDoc;

      const temp = found.parentArray[idx];
      found.parentArray[idx] = found.parentArray[targetIdx];
      found.parentArray[targetIdx] = temp;
      return clone;
    });
  }, [setDocument]);

  // Duplicate node
  const handleDuplicateNode = useCallback((nodeId) => {
    if (nodeId === 'website-header' || nodeId === 'website-footer') {
      showToast('info', 'Website header and footer cannot be duplicated.');
      return;
    }
    setDocument((prevDoc) => {
      const clone = JSON.parse(JSON.stringify(prevDoc));
      const found = findNodeById(clone.sections, nodeId);
      if (!found || !found.parentArray) return prevDoc;

      const dup = duplicateNode(found.node);
      found.parentArray.splice(found.index + 1, 0, dup);
      setSelectedNodeId(dup.id);
      return clone;
    });
    showToast('success', 'Element duplicated');
  }, [setDocument]);

  // Delete node
  const handleDeleteNode = useCallback((nodeId) => {
    if (nodeId === 'website-header' || nodeId === 'website-footer') {
      showToast('info', 'Website header and footer are permanent layout parts.');
      return;
    }
    setDocument((prevDoc) => {
      const clone = JSON.parse(JSON.stringify(prevDoc));
      const found = findNodeById(clone.sections, nodeId);
      if (!found || !found.parentArray) return prevDoc;

      found.parentArray.splice(found.index, 1);
      return clone;
    });
    setSelectedNodeId(null);
    showToast('info', 'Element deleted');
  }, [setDocument]);

  // Toggle Visibility
  const handleToggleVisibility = useCallback((nodeId) => {
    setDocument((prevDoc) => {
      const clone = JSON.parse(JSON.stringify(prevDoc));
      const found = findNodeById(clone.sections, nodeId);
      if (!found) return prevDoc;

      const current = found.node.visibility?.desktop !== false;
      found.node.visibility = {
        ...(found.node.visibility || { desktop: true, tablet: true, mobile: true }),
        desktop: !current,
      };
      return clone;
    });
  }, [setDocument]);

  // Open Media Picker
  const handleOpenMediaPicker = (callback) => {
    setMediaPickerCallback(() => callback);
    setIsMediaPickerOpen(true);
  };

  // Exit Guard
  const handleExit = () => {
    if (computeDocumentFingerprint(doc) !== computeDocumentFingerprint(initialDocument)) {
      if (!window.confirm('You have unsaved changes that will be lost. Discard and exit?')) {
        return;
      }
    }
    onClose();
  };

  const handleDiscard = () => {
    if (window.confirm('Discard all unsaved changes and exit Visual Builder?')) {
      onClose();
    }
  };

  // Save Flow
  const handleSave = async (asDraft = false) => {
    if (!asDraft && !isApproved) {
      showToast('error', 'Please review and approve the preview before saving changes.');
      return;
    }
    const val = validateDocument(doc);
    if (!val.isValid) {
      showToast('error', val.errors[0] || 'Invalid document');
      return;
    }

    try {
      // Convert to legacy shape with canonical sections embedded
      const savedDoc = asDraft ? { ...doc, status: 'draft' } : doc;
      const legacyPayload = canonicalToLegacy(savedDoc);
      const saveHandler = onSaveDocument || onSave;
      if (saveHandler) {
        await saveHandler(legacyPayload, savedDoc);
      }
      showToast('success', 'Page successfully saved!');
    } catch (err) {
      showToast('error', err.message || 'Save failed. Draft preserved.');
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-100 z-50 flex flex-col overflow-hidden font-sans">
      {/* Toast Notification */}
      {notification && (
        <div
          className={`fixed top-20 right-6 z-60 px-4 py-2.5 rounded-xl shadow-lg border text-xs font-bold transition-all flex items-center gap-2 ${
            notification.type === 'error'
              ? 'bg-red-50 text-red-800 border-red-200'
              : notification.type === 'info'
              ? 'bg-blue-50 text-blue-800 border-blue-200'
              : 'bg-emerald-50 text-emerald-800 border-emerald-200'
          }`}
        >
          <span>{notification.type === 'error' ? '⚠️' : '✓'}</span>
          <span>{notification.text}</span>
        </div>
      )}

      {/* 1. TOP TOOLBAR */}
      <BuilderToolbar
        document={doc}
        viewport={viewport}
        setViewport={setViewport}
        isPreviewMode={isPreviewMode}
        setIsPreviewMode={setIsPreviewMode}
        isNavigatorOpen={isNavigatorOpen}
        setIsNavigatorOpen={setIsNavigatorOpen}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        isApproved={isApproved}
        canApprove={canApprove}
        validationErrors={validationErrors}
        onApprovePreview={() => {
          const ok = approvePreview();
          if (ok) showToast('success', 'Preview approved! Save Changes is now enabled.');
          else showToast('error', validationErrors[0] || 'Cannot approve preview');
        }}
        onResetApproval={resetApproval}
        onSave={() => handleSave(false)}
        onSaveDraft={() => handleSave(true)}
        isSaving={isSaving}
        onExit={handleExit}
        onDiscard={handleDiscard}
        onUpdateDocument={setDocument}
      />

      {/* 2. MAIN WORKSPACE */}
      <div className="flex-1 flex min-h-0 overflow-hidden relative">
        {/* LEFT PANEL: Element Library OR Element Settings Inspector */}
        {!isPreviewMode && (
          <aside className="w-80 border-r border-slate-200 bg-white flex flex-col h-full shadow-xs flex-shrink-0 z-30">
            {selectedNode ? (
              <ElementInspector
                selectedNode={selectedNode}
                onUpdateNode={handleUpdateNode}
                onDuplicateNode={handleDuplicateNode}
                onDeleteNode={handleDeleteNode}
                onDeselect={() => setSelectedNodeId(null)}
                onOpenMediaPicker={handleOpenMediaPicker}
              />
            ) : (
              <WidgetLibrary
                onAddWidget={handleAddWidget}
                onAddSection={handleAddSection}
                onInsertSavedBlock={handleInsertSavedBlock}
                client={client}
              />
            )}
          </aside>
        )}

        {/* CENTER: Visual WYSIWYG Canvas */}
        <BuilderCanvas
          document={doc}
          selectedNodeId={selectedNodeId}
          onSelectNode={setSelectedNodeId}
          onMoveNode={handleMoveNode}
          onDuplicateNode={handleDuplicateNode}
          onDeleteNode={handleDeleteNode}
          onAddSection={handleAddSection}
          onAddContainer={handleAddContainer}
          onAddWidgetToContainer={handleAddWidgetToContainer}
          viewport={viewport}
          isPreviewMode={isPreviewMode}
        />

        {/* RIGHT: Layers Navigator Panel */}
        {!isPreviewMode && isNavigatorOpen && (
          <NavigatorTree
            document={doc}
            selectedNodeId={selectedNodeId}
            onSelectNode={setSelectedNodeId}
            onMoveNode={handleMoveNode}
            onDeleteNode={handleDeleteNode}
            onToggleVisibility={handleToggleVisibility}
            onClose={() => setIsNavigatorOpen(false)}
          />
        )}
      </div>

      {/* Media Picker Modal */}
      <MediaPickerModal
        isOpen={isMediaPickerOpen}
        onClose={() => setIsMediaPickerOpen(false)}
        onSelect={(url) => {
          if (mediaPickerCallback) mediaPickerCallback(url);
        }}
      />
    </div>
  );
}
